import { Container } from 'typescript-ioc';
import { IControlDAO } from '../../../../api/control.dao';
import { ControlDAO } from '../../../../api/implementations/control.dao';
import { ATTENDEE_INFO_POPOVER_TRACKING_EVENTS, OpenFrom } from '../../../../tracking-service/tracking-values';
import { alertServerError } from '../../modal/alerts-host/alert-templates';
import { IAttendeeDetailsDTO, IAttendeeNote } from '../../../../api/attendees.dao';
import { IAttendeeAdditionalInfo } from '../../../_events/_manage-event/shared/attendees-section/_attendee/_attendee';
import { action, observable, runInAction } from 'mobx';

class AttendeeColumnPopoverViewmodel {
    @observable
    private isPopoverOpen: Set<string> = new Set();
    @observable
    private isLoading: boolean;
    @observable
    private attendeeAdditionalInfo: IAttendeeAdditionalInfo = null;
    @observable
    private attendeeNotes: IAttendeeNote[] = [];

    constructor(
        private accountId: number,
        private eventId: number,
        private readonly handleEditAttendeeClick: (
            attendeeId: number,
            openFrom: OpenFrom,
            onAttendeeEditSaveCallback?: (attendee: IAttendeeDetailsDTO) => void
        ) => void,
        private readonly handleTrackingEvent: (event: ATTENDEE_INFO_POPOVER_TRACKING_EVENTS, openFrom?: OpenFrom) => void,
        private readonly controlDAO: IControlDAO = Container.get<ControlDAO>(ControlDAO)
    ) { }

    public get loading() {
        return this.isLoading;
    }

    public get attendeeInfo() {
        return this.attendeeAdditionalInfo;
    }

    public get notes() {
        return this.attendeeNotes;
    }

    public isOpen = (meetingAttendeeId: string): boolean => {
        return this.isPopoverOpen.has(meetingAttendeeId);
    };

    @action
    public setOpen = (meetingAttendeeId: string): void => {
        this.isPopoverOpen.add(meetingAttendeeId);
    };

    @action
    public setClose = (): void => {
        this.isPopoverOpen.clear();
        this.attendeeAdditionalInfo = null;
    };

    @action
    public setIsLoading = (value: boolean) => {
        this.isLoading = value;
    };

    public trackEvent = (event: ATTENDEE_INFO_POPOVER_TRACKING_EVENTS, openFrom?: OpenFrom) => {
        this.handleTrackingEvent(event, openFrom);
    };

    @action
    public handleOpenPopover = async (attendeeId: number, meetingAttendeeId: string, openFrom?: OpenFrom): Promise<void> => {
        const isPopoverOpen = this.isOpen(meetingAttendeeId);
        if (!isPopoverOpen) {
            await this.fetchAttendeeInfo(attendeeId, meetingAttendeeId);
            this.handleTrackingEvent(ATTENDEE_INFO_POPOVER_TRACKING_EVENTS.CALL, openFrom);
        }
    };

    @action
    public openAttendeePopover = (
        attendeeId: number,
        openFrom: OpenFrom,
        onAttendeeEditSaveCallback?: (attendee: IAttendeeDetailsDTO) => void
    ) => {
        this.isPopoverOpen.clear();
        this.handleEditAttendeeClick(attendeeId, openFrom, onAttendeeEditSaveCallback);
    };

    @action
    private fetchAttendeeInfo = async (attendeeId: number, meetingAttendeeId: string) => {
        try {
            this.setIsLoading(true);
            this.setClose();
            const data = await this.controlDAO.getControlAttendee({ eventId: this.eventId, accountId: this.accountId, attendeeId: attendeeId });
            runInAction(() => {
                this.attendeeAdditionalInfo = {
                    name: `${data.firstName} ${data.lastName}`,
                    organizationName: data.organizationName,
                    title: data.title,
                    email: data.email,
                    tel: data.phoneNumber,
                };

                this.attendeeNotes = data.previousMeetingAttendeeNotes || [];
            });
            this.setOpen(meetingAttendeeId);
        } catch (e) {
            alertServerError('Error', e);
        } finally {
            this.setIsLoading(false);
        }
    };
}

export { AttendeeColumnPopoverViewmodel };